"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { Smartphone, Globe2, Headset, ArrowRight } from "lucide-react";

/**
 * Travel Connected — homepage "Works with your device" strip per
 * generated_homepage_or_landing.png: a light commercial bar with a
 * compatibility note + device names (iPhone / Pixel / Samsung Galaxy) +
 * network partners + 24/7 support. Server/static. Device names are plain text
 * (no fabricated certification logos).
 */
export default function CompatPreview() {
  const { t } = useTranslations();

  const devices = ["iPhone", "Google Pixel", "Samsung Galaxy"];

  return (
    <section className="pc-trustbar">
      <Reveal as="div" className="container mx-auto px-4 py-8" y={16}>
        <div className="grid lg:grid-cols-[1.2fr_1fr_1fr] gap-6 items-center">
          <div className="flex items-start gap-3">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-card border border-border text-primary shrink-0">
              <Smartphone className="w-5 h-5" />
            </span>
            <div>
              <h2 className="font-bold text-foreground">{t("pc.compatPrev.title", "Works with your device")}</h2>
              <p className="text-sm text-muted-foreground">{t("pc.compatPrev.body", "Compatible with eSIM-supported smartphones and tablets.")}</p>
              <Link href="/compatible-phones/" className="mt-1 inline-flex items-center gap-1 text-sm font-bold text-primary hover:underline group/cc">
                {t("pc.compatPrev.cta", "Check full compatibility list")} <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/cc:translate-x-0.5" />
              </Link>
            </div>
          </div>

          <Stagger as="div" className="flex flex-wrap gap-2" staggerChildren={0.06} amount={0.4}>
            {devices.map((d) => (
              <StaggerItem as="span" key={d} y={10} className="inline-flex items-center rounded-lg border border-border bg-card px-3 h-9 text-sm font-semibold text-foreground/80 transition-colors hover:border-primary/40">{d}</StaggerItem>
            ))}
          </Stagger>

          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80"><Globe2 className="w-4 h-4 text-primary" />{t("pc.compatPrev.partners", "Top network partners worldwide")}</span>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80"><Headset className="w-4 h-4 text-primary" />{t("pc.compatPrev.support", "24/7 customer support")}</span>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
